import { useMemo, useState } from 'react';
import { useLocalization } from '@/hooks/use-localization';
import { validators } from '@/hooks/use-validation';
import { MaritalStatus, HabitationForm, StepCommonProps, Steps, FieldValuesType, EmploymentNeed } from '../../types';
import { Field } from '../../../../../components/forms/components/field';
import { Checkbox } from '@/isomorphic/components/forms/components/checkbox';
import { Select } from '@/isomorphic/components/forms/components/select';
import { Button } from '@/isomorphic/components/typography/button/button';
import { YearMonthSelect } from '../year-month-select';
import { useEmploymentFields } from '../../hooks/use-employment-fields';
import { Arrow } from '../svg';
import clsx from 'clsx';
import { useBrandedColors } from '../../hooks/use-branded-colors';

export const StepOne = ({ step, setStep, fields, updateFields, getLabel, brandColor, scrollToTop }: StepCommonProps) => {
    const { getLocale } = useLocalization();
    const { useBrandedColorsBool } = useBrandedColors({ brandColor });

    const [showValidationErrors, setShowValidationErrors] = useState(false);

    const maritalOptions: MaritalStatus[] = ['SINGLE', 'MARRIED', 'DOMESTIC_PARTNER'];
    const habitationOptions: HabitationForm[] = ['APARTMENT', 'CONDOMINIUM', 'DETACHED_HOUSE'];

    const childrenOptions = Array.from({ length: 11 }).map((_, index) => ({
        value: String(index),
        label: String(index),
    }));

    const handleNext = () => {
        if (validationState.allFieldsValid) {
            setStep((step + 1) as Steps);
            return;
        }

        scrollToTop();
        setShowValidationErrors(true);
    };

    const employmentType = useMemo(() => fields.applicantEmploymentType, [fields.applicantEmploymentType]);
    const { active, options: employmentOptions } = useEmploymentFields(employmentType);

    const validationState = useMemo(() => {
        const requiredFields: Partial<Record<keyof FieldValuesType, boolean>> = {
            phoneMobile: validators.phoneMobile(fields.phoneMobile, getLocale()),
            contactEmail: validators.email(fields.contactEmail),
            applicantMaritalStatus: !!fields.applicantMaritalStatus,
            numberChildrenNotOfAge: !!fields.numberChildrenNotOfAge,
            applicantHabitationForm: !!fields.applicantHabitationForm,
            applicantEmploymentType: !!fields.applicantEmploymentType,
        };

        // Additional validation for employment fields
        const activeFields = Object.keys(active) as (keyof EmploymentNeed)[];
        activeFields.forEach(field => {
            requiredFields[field] = !!fields[field];
        });

        return {
            ...requiredFields,
            allFieldsValid: Object.values(requiredFields).every(Boolean),
        };
    }, [fields, active, validators]);

    return (
        <div className="r-relative r-mx-auto r-max-w-2xl r-px-4">
            <div className="r-flex r-flex-col r-space-y-8">
                <div>
                    <h4 className={clsx('r-mb-3 r-text-xl r-font-semibold', useBrandedColorsBool ? 'r-text-black' : 'r-text-primary-500')}>
                        {getLabel('personalInformation')}
                    </h4>
                    <Field grouped="top" value={fields.governmentId ?? ''} onChange={() => {}} label={getLabel('governmentId')} disabled />
                    <Field
                        required
                        grouped="middle"
                        value={fields.phoneMobile}
                        label={getLabel('phoneMobile')}
                        infoText={getLabel('phoneMobileInfoText')}
                        onChange={value => updateFields({ phoneMobile: value })}
                        validation={v => validators.phoneMobile(v, getLocale())}
                        showRequiredError={showValidationErrors && !validationState.phoneMobile}
                    />
                    <Field
                        required
                        grouped="bottom"
                        value={fields.contactEmail}
                        validation={validators.email}
                        label={getLabel('contactEmail')}
                        infoText={getLabel('contactEmailInfoText')}
                        onChange={value => updateFields({ contactEmail: value })}
                        showRequiredError={showValidationErrors && !validationState.contactEmail}
                    />
                </div>
                <div>
                    <h4 className={clsx('r-font-semibol r-mb-3 r-text-xl', useBrandedColorsBool ? 'r-text-black' : 'r-text-primary-500')}>
                        {getLabel('livingSituation')}
                    </h4>
                    <div className="r-mb-6 r-flex r-flex-col r-gap-2">
                        <p>{`${getLabel('applicantMaritalStatus')} *`}</p>
                        {maritalOptions.map(option => (
                            <Checkbox
                                key={option}
                                type="radio"
                                name="marital-option"
                                brandColor={brandColor}
                                label={getLabel(option)}
                                checked={fields.applicantMaritalStatus === option}
                                onChange={checked => checked && updateFields({ applicantMaritalStatus: option })}
                                showRequiredError={showValidationErrors && !validationState.applicantMaritalStatus}
                            />
                        ))}
                    </div>
                    <div className="r-mb-6">
                        <Select
                            required
                            options={childrenOptions}
                            label={getLabel('numberChildrenNotOfAge')}
                            value={fields.numberChildrenNotOfAge ?? ''}
                            infoText={getLabel('numberChildrenNotOfAgeInfoText')}
                            onChange={value => updateFields({ numberChildrenNotOfAge: String(value) })}
                            showRequiredError={showValidationErrors && !validationState.numberChildrenNotOfAge}
                        />
                    </div>
                    <div className="r-flex r-flex-col r-gap-2">
                        <p>{`${getLabel('applicantHabitationForm')} *`}</p>
                        {habitationOptions.map(option => (
                            <Checkbox
                                key={option}
                                type="radio"
                                brandColor={brandColor}
                                label={getLabel(option)}
                                name="habitation-option"
                                checked={fields.applicantHabitationForm === option}
                                onChange={checked => checked && updateFields({ applicantHabitationForm: option })}
                                showRequiredError={showValidationErrors && !validationState.applicantHabitationForm}
                            />
                        ))}
                    </div>
                </div>
                <div className="r-space-y-4">
                    <h4 className={clsx('r-mb-3 r-text-xl r-font-semibold', useBrandedColorsBool ? 'r-text-black' : 'r-text-primary-500')}>
                        {getLabel('employment')}
                    </h4>
                    <div className="r-mb-10 r-flex r-flex-col r-gap-2">
                        <p>{`${getLabel('employmentType')} *`}</p>
                        {employmentOptions.map(option => (
                            <Checkbox
                                type="radio"
                                key={option.value}
                                brandColor={brandColor}
                                name="employment-option"
                                label={getLabel(option.value)}
                                checked={fields.applicantEmploymentType === option.value}
                                showRequiredError={showValidationErrors && !validationState.applicantEmploymentType}
                                onChange={checked => checked && updateFields({ applicantEmploymentType: option.value })}
                            />
                        ))}
                    </div>
                    {employmentType && (
                        <>
                            {active.employmentTypeQualifying && (
                                <div className="r-py-2">
                                    <label className="r-mb-1 r-block">{getLabel('employmentTypeQualifyingText')} *</label>

                                    <div className="r-flex r-gap-4">
                                        <label className="r-flex r-items-center r-gap-1">
                                            <Checkbox
                                                type="radio"
                                                value="true"
                                                brandColor={brandColor}
                                                name="employmentTypeQualifying"
                                                checked={fields.employmentTypeQualifying === 'true'}
                                                onChange={() => updateFields({ employmentTypeQualifying: 'true' })}
                                                showRequiredError={showValidationErrors && !validationState.employmentTypeQualifying}
                                            />
                                            {getLabel('yes')}
                                        </label>

                                        <label className="r-flex r-items-center r-gap-1">
                                            <Checkbox
                                                type="radio"
                                                value="false"
                                                brandColor={brandColor}
                                                name="employmentTypeQualifying"
                                                checked={fields.employmentTypeQualifying === 'false'}
                                                onChange={() => updateFields({ employmentTypeQualifying: 'false' })}
                                                showRequiredError={showValidationErrors && !validationState.employmentTypeQualifying}
                                            />
                                            {getLabel('no')}
                                        </label>
                                    </div>
                                </div>
                            )}
                            {active.applicantEmployer && (
                                <div className="r-flex r-flex-col r-gap-3">
                                    <Field
                                        required
                                        value={fields.applicantEmployer ?? ''}
                                        label={`${getLabel('applicantEmployer')}`}
                                        infoText={getLabel('applicantEmployerInfoText')}
                                        onChange={value => updateFields({ applicantEmployer: value })}
                                        showRequiredError={showValidationErrors && !validationState.applicantEmployer}
                                    />
                                </div>
                            )}
                            {active.applicantEmploymentYearFrom && (
                                <div className="r-flex r-flex-col r-gap-3">
                                    <YearMonthSelect
                                        fields={fields}
                                        updateFields={updateFields}
                                        labels={{
                                            title: getLabel('employmentYearFromTitle'),
                                            year: getLabel('employmentYearPlaceholder'),
                                            month: getLabel('employmentMonthPlaceholder'),
                                            infoTextYear: getLabel('employmentYearFromInfoText'),
                                            infoTextMonth: getLabel('employmentMonthFromInfoText'),
                                        }}
                                        showRequiredError={showValidationErrors && !validationState.applicantEmploymentYearFrom}
                                    />
                                </div>
                            )}
                            {active.applicantEmploymentYearUntil && (
                                <div className="r-flex r-flex-col r-gap-3">
                                    <YearMonthSelect
                                        future
                                        fields={fields}
                                        updateFields={updateFields}
                                        labels={{
                                            title: getLabel('employmentYearUntilTitle'),
                                            year: getLabel('employmentYearPlaceholder'),
                                            month: getLabel('employmentMonthPlaceholder'),
                                            infoTextYear: getLabel('employmentYearUntilInfoText'),
                                            infoTextMonth: getLabel('employmentMonthUntilInfoText'),
                                        }}
                                        showRequiredError={showValidationErrors && !validationState.applicantEmploymentYearUntil}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            <div className="r-flex r-gap-4 r-pt-14 max-lg:r-flex-col">
                <Button brandColor={brandColor} type="secondary" fullWidth onClick={() => setStep((step - 1) as Steps)}>
                    <div className="r-flex r-gap-2">
                        <Arrow className="r-w-4 r-rotate-180" />
                        <p>{getLabel('backButton')}</p>
                    </div>
                </Button>
                <Button brandColor={brandColor} ariaDisabled={!validationState.allFieldsValid} fullWidth onClick={handleNext}>
                    <div className="r-flex r-gap-2">
                        <p>{getLabel('continueButton')}</p>
                        <Arrow className="r-w-4" />
                    </div>
                </Button>
            </div>
        </div>
    );
};
